import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileSpreadsheet } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const MonthlySOADialog = ({ open, onOpenChange, invoices, whInvoices, onCreated }) => {
  const navigate = useNavigate();
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [customerEmail, setCustomerEmail] = useState('');
  const [creating, setCreating] = useState(false);

  const customers = useMemo(() => {
    const map = {};
    [...(invoices || []), ...(whInvoices || [])].forEach((inv) => {
      const created = (inv.created_at || '').slice(0, 7);
      if (created === month && inv.customer_email) {
        if (!map[inv.customer_email]) {
          map[inv.customer_email] = { name: inv.customer_name, email: inv.customer_email, phone: inv.customer_phone || '', address: inv.customer_address || '', count: 0 };
        }
        map[inv.customer_email].count += 1;
      }
    });
    return Object.values(map).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [invoices, whInvoices, month]);

  const handleCreate = async () => {
    const cust = customers.find((c) => c.email === customerEmail);
    if (!cust) { toast.error('Please select a customer'); return; }
    const [year, m] = month.split('-').map(Number);
    const lastDay = new Date(year, m, 0).getDate();
    setCreating(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/soa/`, {
        customer_name: cust.name,
        customer_email: cust.email,
        customer_phone: cust.phone,
        customer_address: cust.address,
        start_date: `${month}-01`,
        end_date: `${month}-${String(lastDay).padStart(2, '0')}`,
      });
      toast.success(`${res.data.document_number} created — balance due $${(res.data.balance_due || 0).toFixed(2)}`, { duration: 8000 });
      onOpenChange(false);
      onCreated?.();
      navigate(`/admin/billing/soa/${res.data.soa_id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create monthly SOA');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="monthly-soa-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet size={20} className="text-indigo-700" /> Monthly Statement of Account
          </DialogTitle>
          <DialogDescription>
            One click generates an SOA covering all of a customer's invoices and payments for the month (vehicle + warehouse).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Month</Label>
            <Input
              type="month"
              value={month}
              onChange={(e) => { setMonth(e.target.value); setCustomerEmail(''); }}
              data-testid="monthly-soa-month-input"
            />
          </div>
          <div>
            <Label>Customer</Label>
            <select
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              className="w-full h-10 border rounded-md px-3 bg-white text-sm"
              data-testid="monthly-soa-customer-select"
            >
              <option value="">— Select customer —</option>
              {customers.map((c) => (
                <option key={c.email} value={c.email}>{c.name} ({c.count} invoice{c.count > 1 ? 's' : ''})</option>
              ))}
            </select>
            {customers.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">No invoices found in this month.</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleCreate}
            disabled={creating || !customerEmail}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="monthly-soa-generate-btn"
          >
            {creating ? 'Generating...' : 'Generate SOA'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
